import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Avatar from '../../components/ui/Avatar';
import StatusPill from '../../components/ui/StatusPill';
import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconArrowLeft,
  IconCheck,
  IconClock,
  IconUsers,
  IconBuilding,
  IconTractor,
  IconAlertTriangle,
} from '@tabler/icons-react';

export default function ShiftDetail() {
  const { language } = useTranslation();
  const { shiftId } = useParams();
  const navigate = useNavigate();
  const { orgId, userProfile, role } = useAuth();

  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!orgId || !shiftId) return;
      try {
        const snap = await getDoc(doc(db, 'organisations', orgId, 'shifts', shiftId));
        if (snap.exists()) setShift({ id: snap.id, ...snap.data() });
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId, shiftId]);

  const handleCheckIn = async (userId) => {
    setUpdating(true);
    try {
      const updated = shift.assignments.map(a =>
        a.userId === userId ? {
          ...a,
          checkedIn: true,
          checkedInAt: new Date().toISOString(),
        } : a
      );
      await updateDoc(doc(db, 'organisations', orgId, 'shifts', shiftId), {
        assignments: updated,
        status: 'active',
        updatedAt: serverTimestamp(),
      });
      setShift(prev => ({ ...prev, assignments: updated, status: 'active' }));
      toast.success('Checked in');
    } catch (err) {
      console.error(err);
      toast.error('Failed to check in');
    }
    setUpdating(false);
  };

  const handleCheckOut = async (userId) => {
    setUpdating(true);
    try {
      const updated = shift.assignments.map(a =>
        a.userId === userId ? {
          ...a,
          checkedOut: true,
          checkedOutAt: new Date().toISOString(),
        } : a
      );
      const allOut = updated.every(a => a.checkedOut);
      await updateDoc(doc(db, 'organisations', orgId, 'shifts', shiftId), {
        assignments: updated,
        status: allOut ? 'completed' : 'active',
        updatedAt: serverTimestamp(),
      });
      setShift(prev => ({ ...prev, assignments: updated, status: allOut ? 'completed' : 'active' }));
      toast.success('Checked out');
    } catch (err) {
      console.error(err);
      toast.error('Failed to check out');
    }
    setUpdating(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString(language, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '—';
    const d = new Date(isoStr);
    return d.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' });
  };

  const checkedIn = shift?.assignments?.filter(a => a.checkedIn).length || 0;
  const checkedOut = shift?.assignments?.filter(a => a.checkedOut).length || 0;
  const total = shift?.assignments?.length || 0;

  const isToday = shift?.date === new Date().toISOString().split('T')[0];
  const isMyShift = shift?.assignments?.find(a => a.userId === userProfile?.id);

  if (loading) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>
        Loading...
      </div>
    </Layout>
  );

  if (!shift) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/shifts')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Shifts
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>Shift not found</div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 640, margin: '0 auto' }}>

        <div onClick={() => navigate('/shifts')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Shifts
        </div>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h1 style={{ fontSize: 22, fontWeight: 400 }}>{shift.siteName || 'Shift'}</h1>
              <StatusPill status={shift.status || 'scheduled'} size="lg" />
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              {formatDate(shift.date)} · {shift.startTime} — {shift.endTime}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Assigned', value: total, colour: 'var(--color-text-primary)' },
            { label: 'Checked in', value: checkedIn, colour: checkedIn > 0 ? 'var(--color-success)' : 'var(--color-text-tertiary)' },
            { label: 'Checked out', value: checkedOut, colour: checkedOut > 0 ? 'var(--color-brand)' : 'var(--color-text-tertiary)' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
              <div style={{ fontSize: 28, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Shift details */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Details</div>
          {[
            { icon: IconBuilding, label: 'Site',       value: shift.siteName || '—'                   },
            { icon: IconClock,    label: 'Date',        value: formatDate(shift.date)                   },
            { icon: IconClock,    label: 'Start',       value: shift.startTime || '—'                  },
            { icon: IconClock,    label: 'End',         value: shift.endTime || '—'                    },
            { icon: IconUsers,    label: 'Created by',  value: shift.createdByName || '—'              },
          ].map(row => {
            const Icon = row.icon;
            return (
              <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                <Icon size={14} color="var(--color-text-tertiary)" stroke={1.5} />
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', width: 80, flexShrink: 0 }}>{row.label}</span>
                <span style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{row.value}</span>
              </div>
            );
          })}
          {shift.notes && (
            <div style={{ padding: '10px 0' }}>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>Notes</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.6 }}>{shift.notes}</div>
            </div>
          )}
        </Card>

        {/* Assignments */}
        <Card>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Operators</div>
          {!shift.assignments?.length ? (
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', textAlign: 'center', padding: '16px 0' }}>
              No operators assigned to this shift
            </div>
          ) : shift.assignments.map((a, i) => (
            <div
              key={a.userId}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 0',
                borderBottom: i < shift.assignments.length - 1 ? '0.5px solid var(--color-border-light)' : 'none',
              }}
            >
              <Avatar name={a.userName} size="md" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: 'var(--color-text-primary)', marginBottom: 2 }}>{a.userName}</div>
                <div style={{ display: 'flex', gap: 10, fontSize: 11, color: 'var(--color-text-tertiary)', flexWrap: 'wrap' }}>
                  {a.machineName && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <IconTractor size={11} stroke={1.5} /> {a.machineName}
                    </span>
                  )}
                  {a.checkedIn && (
                    <span style={{ color: 'var(--color-success)' }}>
                      In: {formatTime(a.checkedInAt)}
                    </span>
                  )}
                  {a.checkedOut && (
                    <span style={{ color: 'var(--color-brand)' }}>
                      Out: {formatTime(a.checkedOutAt)}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {!a.checkedIn && isToday && (role === 'manager' || role === 'org_admin' || a.userId === userProfile?.id) && (
                  <Button
                    size="sm"
                    variant="success-outline"
                    onClick={() => handleCheckIn(a.userId)}
                    disabled={updating}
                    icon={<IconCheck size={12} stroke={2} />}
                  >
                    Check in
                  </Button>
                )}
                {a.checkedIn && !a.checkedOut && isToday && (role === 'manager' || role === 'org_admin' || a.userId === userProfile?.id) && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleCheckOut(a.userId)}
                    disabled={updating}
                  >
                    Check out
                  </Button>
                )}
                {a.checkedOut && (
                  <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: 'var(--color-bg-secondary)', color: 'var(--color-text-tertiary)' }}>
                    Complete
                  </span>
                )}
                {a.checkedIn && !a.checkedOut && !isToday && (
                  <StatusPill status="active" />
                )}
                {!a.checkedIn && !isToday && (
                  <StatusPill status="pending" />
                )}
              </div>
            </div>
          ))}
        </Card>

      </div>
    </Layout>
  );
}